import { Button, Card, DatePicker } from 'antd';
import CreditCardDiv from '../../../components/CreditCardDiv';
import { FC, useEffect, useState } from 'react';
import { mutationNotification, useDelete, usePut } from '../../../hooks/useApi';
import Swal from 'sweetalert2';
import PaymentProcessingDisclosureModal from '../modals/PaymentProcessingDisclosureModal';
import dayjs, { Dayjs } from 'dayjs';
import { Col, Row } from 'react-bootstrap';
import { useQueryClient } from 'react-query';

type CurrentAutomaticPaymentProps = {
  data: any;
  onDeAuthorize: () => void;
};

const CurrentAutomaticPayment: FC<CurrentAutomaticPaymentProps> = ({
  data,
  onDeAuthorize,
}) => {
  const queryClient = useQueryClient();
  const [
    paymentProcessingDisclosureModalvisible,
    setPaymentProcessingDisclosureModalvisible,
  ] = useState(false);

  const [isEditingEndDate, setIsEditingEndDate] = useState(false);
  const [newEndDate, setNewEndDate] = useState<Dayjs | null>(null);

  const openPaymentProcessingDisclosureModal = (ev: any) => {
    ev.preventDefault();
    setPaymentProcessingDisclosureModalvisible(true);
  };

  const removeIntentDelete = useDelete<any>(
    '/v1/auth/dashboard/auto-pay/removeIntent/' + data.defaultPaymentMethod.id
  );

  const updateEndDatePut = usePut<any, any>(
    '/v1/auth/dashboard/auto-pay/updateEndDate/' + data.defaultPaymentMethod.id
  );

  useEffect(() => {
    mutationNotification(removeIntentDelete, {
      success: {
        title: 'Success',
        description: 'Payment de-authorized successfully',
      },
      error: {
        title: 'Error de-authorizing payment method',
      },
    });

    if (removeIntentDelete.isSuccess) {
      onDeAuthorize();
    }
  }, [removeIntentDelete.isSuccess, removeIntentDelete.isError]);

  useEffect(() => {
    mutationNotification(updateEndDatePut, {
      success: {
        title: 'Success',
        description: 'End date updated successfully',
      },
      error: {
        title: 'Error updating end date',
      },
    });

    if (updateEndDatePut.isSuccess) {
      setIsEditingEndDate(false);
      // Invalidate the auto-pay query to reload fresh data
      queryClient.invalidateQueries([
        '/v1/auth/dashboard/auto-pay/checkLoggedInUser',
      ]);
    }
  }, [updateEndDatePut.isSuccess, updateEndDatePut.isError, queryClient]);

  const handleDeAuthorize = async (ev: any) => {
    ev.preventDefault();

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to de-autorize?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'red',
    });

    if (result.isConfirmed) {
      removeIntentDelete.mutate({});
    }
  };

  const handleUpdateEndDate = async () => {
    if (!newEndDate) {
      return;
    }

    updateEndDatePut.mutate({
      endDate: dayjs(newEndDate).endOf('day').toISOString(),
    });
  };

  const handleEditEndDate = () => {
    setNewEndDate(
      data.activeScheduledPayment?.endDate
        ? dayjs(data.activeScheduledPayment.endDate)
        : null
    );
    setIsEditingEndDate(true);
  };

  const handleCancelEdit = () => {
    setIsEditingEndDate(false);
    setNewEndDate(null);
  };

  // Check if the end date has expired
  const isEndDateExpired = () => {
    if (!data.activeScheduledPayment?.endDate) return false;
    return dayjs(data.activeScheduledPayment.endDate).isBefore(dayjs(), 'day');
  };

  // Format the end date for display
  const formatEndDate = (endDate: string) => {
    return dayjs(endDate).format('MMMM D, YYYY');
  };

  return (
    <>
      <Card
        id={'current-automatic-payment'}
        title={'Your Current Automatic Payment'}
      >
        <p>
          This is the card that you authorized for automatic payments. If you
          need to use a different card, you will need to first de-authorize this
          one.
        </p>

        <p>
          If you need to stop your automatic payments for any reason, you just
          need to de-authorize this card.
        </p>

        <hr />

        {/* End Date Display - Gigantic Bold Font */}
        {data.activeScheduledPayment?.endDate && (
          <div className={'text-center pt-3 pb-3'}>
            <div
              style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: isEndDateExpired() ? '#ff4d4f' : '#52c41a',
                marginBottom: '10px',
              }}
            >
              Auto Pay {isEndDateExpired() ? 'EXPIRED' : 'Active Until'}
            </div>

            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: isEndDateExpired() ? '#ff4d4f' : '#1890ff',
              }}
            >
              {formatEndDate(data.activeScheduledPayment.endDate)}
            </div>

            {/* Edit End Date Section */}
            <div className={'pt-3'}>
              {!isEditingEndDate ? (
                <Button
                  type={'primary'}
                  onClick={handleEditEndDate}
                  style={{ marginRight: '10px' }}
                >
                  Edit End Date
                </Button>
              ) : (
                <Row>
                  <Col md={6}>
                    <DatePicker
                      className={'w-100'}
                      value={newEndDate}
                      onChange={(date) => setNewEndDate(date)}
                      picker={'date'}
                      disabledDate={(current) =>
                        current && current < dayjs().endOf('day')
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Button
                      type={'primary'}
                      onClick={handleUpdateEndDate}
                      disabled={!newEndDate || updateEndDatePut.isLoading}
                      loading={updateEndDatePut.isLoading}
                      style={{ marginRight: '10px' }}
                    >
                      Update
                    </Button>
                    <Button type={'default'} onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          </div>
        )}

        <hr />

        <div className={'pt-3'}>
          <CreditCardDiv
            creditCard={{
              holderName:
                data?.defaultPaymentMethod?.billing_details?.name ||
                data?.customer.name,
              number: `**** **** **** ${data?.defaultPaymentMethod.card.last4}`,
              expiryMonth: data?.defaultPaymentMethod.card.exp_month,
              expiryYear: data?.defaultPaymentMethod.card.exp_year,
              ccv: '',
              cpfCnpj: '',
              expiryDate: `${data?.defaultPaymentMethod.card.exp_month}/${data?.defaultPaymentMethod.card.exp_year}`,
              brand: data?.defaultPaymentMethod.card.brand,
            }}
          />
        </div>

        <div className={'text-center pt-3 pb-3'}>
          <Button
            style={{ width: '257px' }}
            type={'dashed'}
            onClick={handleDeAuthorize}
          >
            De-Authorize?
          </Button>
        </div>

        <hr />

        <p>
          {`If you haven't already, please take a moment to read our`}{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            onClick={openPaymentProcessingDisclosureModal}
            href={'#'}
            style={{ color: '#703895' }}
          >
            Payment Processing Disclosure
          </a>
          .
        </p>
      </Card>

      <PaymentProcessingDisclosureModal
        visible={paymentProcessingDisclosureModalvisible}
        onClose={() => setPaymentProcessingDisclosureModalvisible(false)}
      />
    </>
  );
};

export default CurrentAutomaticPayment;
